import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";

const TaxComparisonPage = () => {
    const { id: businessId } = useParams();

    const [employeeTaxInfo, setEmployeeTaxInfo] = useState([]);
    const [employerTaxInfo, setEmployerTaxInfo] = useState(null);
    const [groupedEmployees, setGroupedEmployees] = useState({});
    const [citiesData, setCitiesData] = useState([]);
    const [fedTaxData, setFedTaxData] = useState([]);
    const [cityTaxData, setCityTaxData] = useState([]);
    const [countyTaxData, setCountyTaxData] = useState([]);
    const [taxableCounties, setTaxableCounties] = useState([]);
    const [stateTaxData, setStateTaxData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);


    const [narrowedData, setNarrowedData] = useState({
            withholdingStates: {},
            unemploymentStates: {},
            diNeededStates: {},
        });

        const allStates = new Set([
            ...Object.keys(narrowedData.withholdingStates),
            ...Object.keys(narrowedData.unemploymentStates),
            ...Object.keys(narrowedData.diNeededStates),
        ]);

        const allComparisonStates = new Set([
            ...Object.keys(narrowedData.withholdingStates),
            ...Object.keys(narrowedData.unemploymentStates),
            ...Object.keys(narrowedData.diNeededStates),
            ...stateTaxData.map((data) => data.state), // Include states from available tax data
        ]);
        

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const [taxInfoResponse, federalResponse, cityResponse, countyResponse, stateResponse] = await Promise.all([
                    api.get(`/tax-info/${businessId}`),
                    api.get(`/get-federal-data/${businessId}`), // Fetch main tax info
                    api.get(`/get-city-data/${businessId}`), // Fetch city tax info
                    api.get(`/get-county-data/${businessId}`), // Fetch county tax info
                    api.get(`/get-state-data/${businessId}`), // Fetch state tax info
                ]);

                const taxInfo = taxInfoResponse.data.taxInfo;


                // Transform grouped data into narrowedData
                const withholdingStates = {};
                const unemploymentStates = {};
                const diNeededStates = {};

                setEmployeeTaxInfo(taxInfo.employees || []);
                setEmployerTaxInfo(taxInfo.employer || null);
                setGroupedEmployees(taxInfo.groupedEmployees || {});
                setCitiesData(taxInfo.taxableCities || {});
                setTaxableCounties(taxInfo.taxableCounties || {});
                setFedTaxData(federalResponse.data || []);
                setCityTaxData(cityResponse.data || []);
                setCountyTaxData(countyResponse.data || []);
                setStateTaxData(stateResponse.data || []);


                Object.entries(taxInfo.groupedEmployees || {}).forEach(([state, workLocs]) => {
                    Object.entries(workLocs).forEach(([workLoc, mandates]) => {
                        Object.entries(mandates).forEach(([mandate, taxRequireds]) => {
                            Object.entries(taxRequireds).forEach(([taxRequired, groupedData]) => {
                                if (groupedData.stateWithholding) {
                                    withholdingStates[groupedData.stateWithholding] =
                                        (withholdingStates[groupedData.stateWithholding] || 0) +
                                        groupedData.count;
                                }
                                if (groupedData.stateUnemployment) {
                                    unemploymentStates[groupedData.stateUnemployment] =
                                        (unemploymentStates[groupedData.stateUnemployment] || 0) +
                                        groupedData.count;
                                }
                                if (groupedData.diNeeded) {
                                    diNeededStates[groupedData.diNeeded] =
                                        (diNeededStates[groupedData.diNeeded] || 0) + groupedData.count;
                                }
                            });
                        });
                    });
                });
                setNarrowedData({ withholdingStates, unemploymentStates, diNeededStates });
            } catch (error) {
                console.error("Error fetching data:", error);
                setError("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [businessId]);

    if (loading) return <p>Loading data...</p>;
    if (error) return <p>{error}</p>;

    return (
        <div style={{ padding: "20px" }}>
            <h2>Tax Comparison Page</h2>

            {/* Employer Tax Information */}
            <div>
                <h3>Employer Tax Information</h3>
                {employerTaxInfo ? (
                    <table>
                        <thead>
                            <tr>
                                <th>Business State</th>
                                <th>Business County</th>
                                <th>Convenience Rule Applies</th>
                                <th>DI Applies</th>
                                <th>Requires Work State Tax</th>
                                <th>Reciprocity States</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{employerTaxInfo.business_state}</td>
                                <td>{employerTaxInfo.business_county}</td>
                                <td>{employerTaxInfo.convenience_rule_applies ? "Yes" : "No"}</td>
                                <td>{employerTaxInfo.di_applies ? "Yes" : "No"}</td>
                                <td>{employerTaxInfo.requires_work_state_tax ? "Yes" : "No"}</td>
                                <td>{employerTaxInfo.reciprocity_states || "N/A"}</td>
                            </tr>
                        </tbody>
                    </table>
                ) : (
                    <p>No employer tax information available.</p>
                )}
            </div>

            {/* Employee Tax Information */}
            <div>
                <h3>Employee Tax Information</h3>
                {employeeTaxInfo.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th>Employee State</th>
                                <th>Employee County</th>
                                <th>Work Location</th>
                                <th>Employer Mandated</th>
                                <th>DI Applies</th>
                                <th>Requires Work State Tax</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeeTaxInfo.map((employee, index) => (
                                <tr key={index}>
                                    <td>{employee.employee_state}</td>
                                    <td>{employee.employee_county}</td>
                                    <td>{employee.work_loc}</td>
                                    <td>{employee.employer_mandated ? "Yes" : "No"}</td>
                                    <td>{employee.di_applies ? "Yes" : "No"}</td>
                                    <td>{employee.requires_work_state_tax ? "Yes" : "No"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No employee tax information available.</p>
                )}
            </div>


            {/* Cities Tax Information */}
            <div>
                <h3>Cities Tax Information</h3>
                {citiesData.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th>City Name</th>
                                <th>City State</th>
                            </tr>
                        </thead>
                        <tbody>
                            {citiesData.map((city) => (
                                <tr key={city}>
                                    <td>{city.city_name}</td>
                                    <td>{city.state_code}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No cities tax information available.</p>
                )}
            </div>

            {/* Counties Tax Information */}
<div>
    {taxableCounties.length > 0 ? (
        <>
            <h3>
                Counties Tax Information
            </h3>
            <table>
                <thead>
                    <tr>
                        <th>County Name</th>
                        <th>State</th>
                    </tr>
                </thead>
                <tbody>
                    {taxableCounties.flatMap((countyData) =>
                        countyData.counties.map((countyKey, index) => {
                            const [state, county] = countyKey.split("_");
                            return (
                                <tr key={`${countyKey}-${index}`}>
                                    <td>{county}</td>
                                    <td>{state}</td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </>
    ) : null}
</div>



            {/* Grouped Employee Information */}
            <div>
                <h3>Grouped Employee Information</h3>
                <table>
                    <thead>
                        <tr>
                            <th>State</th>
                            <th>State Withholding</th>
                            <th>State Unemployment</th>
                            <th>DI Needed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(groupedEmployees).flatMap(([state, workLocs]) =>
                            Object.entries(workLocs).flatMap(([workLoc, mandates]) =>
                                Object.entries(mandates).flatMap(([mandate, taxRequireds]) =>
                                    Object.entries(taxRequireds).map(([taxRequired, data]) => (
                                        <tr key={`${state}-${workLoc}-${mandate}-${taxRequired}`}>
                                            <td>{state}</td>
                                            <td>{data.stateWithholding || "N/A"}</td>
                                            <td>{data.stateUnemployment || "N/A"}</td>
                                            <td>{data.diNeeded || "N/A"}</td>
                                        </tr>
                                    ))
                                )
                            )
                        )}
                    </tbody>
                </table>
            </div>


             {/* State Taxes Section */}
             <div style={{ margin: "3%" }}>
                <h3>
                    Narrowed States
                </h3>
                <div>
                <table>
                        <thead>
                            <tr>
                                <th>State</th>
                                <th>Withholding States</th>
                                <th>Unemployment States</th>
                                <th>DI Needed</th>
                            </tr>
                        </thead>
                        <tbody>

                        {Array.from(allStates).map((state) => (
    <tr key={state}>
        <td>{state}</td>
        <td>{narrowedData.withholdingStates[state] || "N/A"}</td>
        <td>{narrowedData.unemploymentStates[state] || "N/A"}</td>
        <td>{narrowedData.diNeededStates[state] || "N/A"}</td>
    </tr>
))}
                        {/* {Object.keys(narrowedData.withholdingStates).map((state) => (
    <tr key={state}>
        <td>{state}</td>
        <td>{narrowedData.withholdingStates[state] || "N/A"}</td>
        <td>{narrowedData.unemploymentStates[state] || "N/A"}</td>
        <td>{narrowedData.diNeededStates[state] || "N/A"}</td>
    </tr>
))} */}

                    </tbody>
                    </table>
                </div>
            </div>



            <div>
                    <h3>Federal Tax Information</h3>
                    {fedTaxData ? (
                        <table>
                            <thead>
                                <tr>
                                    <th>FEIN</th>
                                    <th>Filing Frequency</th>
                                    <th>Unemployment Exempt</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{fedTaxData.federal_id || "N/A"}</td>
                                    <td>{fedTaxData.filing_frequency || "N/A"}</td>
                                    <td>{fedTaxData.unemployment_exempt === 1 ? "Yes" : "No"}</td>
                                </tr>
                            </tbody>
                        </table>
                    ) : (
                        <p>No Federal tax information available.</p>
                    )}
                </div>




            {/* City Tax Information */}
            <div>
                <h3>City Tax Information</h3>
                {cityTaxData.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th>City</th>
                                <th>State</th>
                                <th>Account Number</th>
                                <th>Deposit Frequency</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cityTaxData.map((city, index) => (
                                <tr key={index}>
                                    <td>{city.city_name}</td>
                                    <td>{city.state_code}</td>
                                    <td>{city.account_number}</td>
                                    <td>{city.deposit_frequency}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No city tax information available.</p>
                )}
            </div>
            






            {/* County Tax Information */}
            <div>
                <h3>County Tax Information</h3>
                {countyTaxData.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th>County</th>
                                <th>State</th>
                                <th>Account Number</th>
                            </tr>
                        </thead>
                        <tbody>
                            {countyTaxData.map((county, index) => (
                                <tr key={index}>
                                    <td>{county.county_name}</td>
                                    <td>{county.county_state}</td>
                                    <td>{county.account_number}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No county tax information available.</p>
                )}
            </div>

            {/* State Tax Information */}
            <div>
                <h3>State Tax Information</h3>
                {stateTaxData.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th>State</th>
                                <th>Withholding Account Number</th>
                                <th>UIA Account Number</th>
                                <th>Unemployment Tax Rate</th>
                                <th>DI Applies</th>
                                <th>DI Employer Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stateTaxData.map((state, index) => (
                                <tr key={index}>
                                    <td>{state.state}</td>
                                    <td>{state.withholding_account_number}</td>
                                    <td>{state.uia_account_number}</td>
                                    <td>{state.unemployment_tax_rate}</td>
                                    <td>{state.di_handling}</td>
                                    <td>{state.employer_rate}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No state tax information available.</p>
                )}
            </div>

            {/* Federal Tax Comparison */}
<div>
    <h3>Federal Tax Comparison</h3>
    <table>
        <thead>
            <tr>
                <th>Field</th>
                <th>Required Data</th>
                <th>Available Data</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td>Federal ID (FEIN)</td>
                <td>{employerTaxInfo?.federal_id || "N/A"}</td>
                <td>{fedTaxData?.federal_id || "N/A"}</td>
            </tr>
            <tr>
                <td>Filing Frequency</td>
                <td>{employerTaxInfo?.filing_frequency || "N/A"}</td>
                <td>{fedTaxData?.filing_frequency || "N/A"}</td>
            </tr>
            <tr>
                <td>Unemployment Exempt</td>
                <td>{employerTaxInfo?.unemployment_exempt ? "Yes" : "No"}</td>
                <td>{fedTaxData?.unemployment_exempt === 1 ? "Yes" : "No"}</td>
            </tr>
        </tbody>
    </table>
</div>


{/* City Tax Comparison */}
<div>
    <h3>City Tax Comparison</h3>
    {citiesData.length > 0 ? (
        <table>
            <thead>
                <tr>
                    <th>Required City</th>
                    <th>Required State</th>
                    <th>Available City</th>
                    <th>Available State</th>
                    <th>Match Status</th>
                </tr>
            </thead>
            <tbody>
                {citiesData.map((city, index) => {
                    const availableCity = cityTaxData.find(
                        (data) =>
                            data.city_name === city.city_name &&
                            data.state_code === city.state_code
                    );

                    // Determine if the city and state match
                    const isMatch = availableCity !== undefined;

                    return (
                        <tr key={index}>
                            <td>{city.city_name}</td>
                            <td>{city.state_code}</td>
                            <td>{availableCity?.city_name || "N/A"}</td>
                            <td>{availableCity?.state_code || "N/A"}</td>
                            <td style={{ textAlign: "center" }}>
                                {isMatch ? (
                                    <span style={{ color: "green" }}>✔️</span>
                                ) : (
                                    <span style={{ color: "red" }}>❌</span>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    ) : (
        <p>No city tax data to compare.</p>
    )}
</div>

{/* Missing Fields Form for Mismatched Cities */}
{citiesData.map((city, index) => {
    const availableCity = cityTaxData.find(
        (data) =>
            data.city_name === city.city_name &&
            data.state_code === city.state_code
    );

    // Generate the form only if the city does not match
    if (!availableCity) {
        return (
            <div
                key={index}
                style={{ margin: "10px 0", border: "1px solid #ccc", padding: "10px" }}
            >
                <h5>{city.city_name}, {city.state_code} - Fill Missing City Tax Information</h5>
                <div>
                    <label>Account Number:</label>
                    <input type="text" placeholder="Enter Account Number" />
                </div>
                <div>
                    <label>Deposit Frequency:</label>
                    <select>
                        <option>Select Frequency</option>
                        <option>Monthly</option>
                        <option>Quarterly</option>
                    </select>
                </div>
            </div>
        );
    }

    return null;
})}


{/* County Tax Comparison */}
<div>
    <h3>County Tax Comparison</h3>
    {taxableCounties.length > 0 ? (
        <table>
            <thead>
                <tr>
                    <th>Required County</th>
                    <th>Required State</th>
                    <th>Available County</th>
                    <th>Available State</th>
                    <th>Available Account</th>
                    <th>Match Status</th>
                </tr>
            </thead>
            <tbody>
                {taxableCounties.flatMap((countyData) =>
                    countyData.counties.map((countyKey, index) => {
                        const [state, county] = countyKey.split("_");
                        const availableCounty = countyTaxData.find(
                            (data) =>
                                data.county_name === county &&
                                data.county_state === state
                        );

                        // Determine if the county and state match
                        const isMatch = availableCounty !== undefined;

                        return (
                            <tr key={`${countyKey}-${index}`}>
                                <td>{county}</td>
                                <td>{state}</td>
                                <td>{availableCounty?.county_name || "N/A"}</td>
                                <td>{availableCounty?.county_state || "N/A"}</td>
                                <td>{availableCounty?.account_number || "N/A"}</td>
                                <td style={{ textAlign: "center" }}>
                                    {isMatch ? (
                                        <span style={{ color: "green" }}>✔️</span>
                                    ) : (
                                        <span style={{ color: "red" }}>❌</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })
                )}
            </tbody>
        </table>
    ) : (
        <p>No county tax data to compare.</p>
    )}
</div>

{/* Missing Fields Form for Mismatched Counties */}
{taxableCounties.flatMap((countyData) =>
    countyData.counties.map((countyKey, index) => {
        const [state, county] = countyKey.split("_");
        const availableCounty = countyTaxData.find(
            (data) =>
                data.county_name === county && data.county_state === state
        );

        // Generate the form only if the county does not match
        if (!availableCounty) {
            return (
                <div
                    key={`${countyKey}-${index}`}
                    style={{
                        margin: "10px 0",
                        border: "1px solid #ccc",
                        padding: "10px",
                    }}
                >
                    <h5>
                        {county}, {state} - Fill Missing County Tax Information
                    </h5>
                    <div>
                        <label>County Tax Account Number:</label>
                        <input
                            type="text"
                            placeholder="Enter County Tax Account Number"
                        />
                    </div>
                </div>
            );
        }

        return null;
    })
)}


{/* State Tax Comparison */}
{/* <div>
    <h3>State Tax Comparison</h3>
    <table>
        <thead>
            <tr>
                <th>State</th>
                <th>Withholding States (Required)</th>
                <th>Withholding States (Available)</th>
                <th>Withholding Status</th>
                <th>Unemployment States (Required)</th>
                <th>Unemployment States (Available)</th>
                <th>Available Unemployment Tax Rate</th>
                <th>Unemployment Status</th>
                <th>DI Needed (Required)</th>
                <th>DI Needed (Available)</th>
                <th>Available DI Employer Rate</th>
                <th>DI Status</th>
            </tr>
        </thead>
        <tbody>
            {Object.keys(narrowedData.withholdingStates).map((state) => {
                const withholdingRequired = narrowedData.withholdingStates[state] || "N/A";
                const unemploymentRequired = narrowedData.unemploymentStates[state] || "N/A";
                const diNeededRequired = narrowedData.diNeededStates[state] || "N/A";

                const availableState = stateTaxData.find((data) => data.state === state);
                const withholdingAvailable = availableState?.withholding_account_number || "N/A";
                const unemploymentAvailable = availableState?.uia_account_number || "N/A";
                const unemploymentTaxRateAvailable = availableState?.unemployment_tax_rate || "N/A";
                const diNeededAvailable = availableState?.di_handling || "N/A";
                const diEmployerRateAvailable = availableState?.employer_rate || "N/A";

                // Determine pass/fail status
                const withholdingStatus =
                    withholdingRequired === "N/A" || withholdingAvailable !== "N/A"
                        ? <span style={{ color: "green" }}>✔️</span>
                        : <span style={{ color: "red" }}>❌</span>;

                const unemploymentStatus =
                    unemploymentRequired === "N/A" ||
                    (unemploymentAvailable !== "N/A" && unemploymentTaxRateAvailable !== "N/A")
                        ? <span style={{ color: "green" }}>✔️</span>
                        : <span style={{ color: "red" }}>❌</span>;

                const diStatus =
                    diNeededRequired === "N/A" ||
                    (diNeededAvailable !== "N/A" || diEmployerRateAvailable !== "N/A")
                        ? <span style={{ color: "green" }}>✔️</span>
                        : <span style={{ color: "red" }}>❌</span>;

                return (
                    <tr key={state}>
                        <td>{state}</td>
                        <td>{withholdingRequired}</td>
                        <td>{withholdingAvailable}</td>
                        <td>{withholdingStatus}</td>
                        <td>{unemploymentRequired}</td>
                        <td>{unemploymentAvailable}</td>
                        <td>{unemploymentTaxRateAvailable}</td>
                        <td>{unemploymentStatus}</td>
                        <td>{diNeededRequired}</td>
                        <td>{diNeededAvailable}</td>
                        <td>{diEmployerRateAvailable}</td>
                        <td>{diStatus}</td>
                    </tr>
                );
            })}
        </tbody>
    </table>
</div> */}

{/* State Tax Comparison */}
<div>
    <h3>State Tax Comparison</h3>
    <table>
        <thead>
            <tr>
                <th>State</th>
                <th>Withholding States (Required)</th>
                <th>Withholding States (Available)</th>
                <th>Withholding Status</th>
                <th>Unemployment States (Required)</th>
                <th>Unemployment States (Available)</th>
                <th>Available Unemployment Tax Rate</th>
                <th>Unemployment Status</th>
                <th>DI Needed (Required)</th>
                <th>DI Needed (Available)</th>
                <th>Available DI Employer Rate</th>
                <th>DI Status</th>
            </tr>
        </thead>
        <tbody>
            {Array.from(allComparisonStates).map((state) => {
                const withholdingRequired = narrowedData.withholdingStates[state] || "N/A";
                const unemploymentRequired = narrowedData.unemploymentStates[state] || "N/A";
                const diNeededRequired = narrowedData.diNeededStates[state] || "N/A";

                const availableState = stateTaxData.find((data) => data.state === state);
                const withholdingAvailable = availableState?.withholding_account_number || "N/A";
                const unemploymentAvailable = availableState?.uia_account_number || "N/A";
                const unemploymentTaxRateAvailable = availableState?.unemployment_tax_rate || "N/A";
                const diNeededAvailable = availableState?.di_handling || "N/A";
                const diEmployerRateAvailable = availableState?.employer_rate || "N/A";

                const withholdingStatus =
                    withholdingRequired === "N/A" || withholdingAvailable !== "N/A"
                        ? <span style={{ color: "green" }}>✔️</span>
                        : <span style={{ color: "red" }}>❌</span>;

                const unemploymentStatus =
                    unemploymentRequired === "N/A" ||
                    (unemploymentAvailable !== "N/A" && unemploymentTaxRateAvailable !== "N/A")
                        ? <span style={{ color: "green" }}>✔️</span>
                        : <span style={{ color: "red" }}>❌</span>;

                const diStatus =
                    diNeededRequired === "N/A" ||
                    (diNeededAvailable !== "N/A" || diEmployerRateAvailable !== "N/A")
                        ? <span style={{ color: "green" }}>✔️</span>
                        : <span style={{ color: "red" }}>❌</span>;

                return (
                    <tr key={state}>
                        <td>{state}</td>
                        <td>{withholdingRequired}</td>
                        <td>{withholdingAvailable}</td>
                        <td>{withholdingStatus}</td>
                        <td>{unemploymentRequired}</td>
                        <td>{unemploymentAvailable}</td>
                        <td>{unemploymentTaxRateAvailable}</td>
                        <td>{unemploymentStatus}</td>
                        <td>{diNeededRequired}</td>
                        <td>{diNeededAvailable}</td>
                        <td>{diEmployerRateAvailable}</td>
                        <td>{diStatus}</td>
                    </tr>
                );
            })}
        </tbody>
    </table>
</div>

{/* Missing Fields Form for States */}
{Array.from(allComparisonStates).map((state) => {
    const withholdingRequired = narrowedData.withholdingStates[state] || "N/A";
    const unemploymentRequired = narrowedData.unemploymentStates[state] || "N/A";
    const diNeededRequired = narrowedData.diNeededStates[state] || "N/A";

    const availableState = stateTaxData.find((data) => data.state === state);
    const withholdingAvailable = availableState?.withholding_account_number || "N/A";
    const unemploymentAvailable = availableState?.uia_account_number || "N/A";
    const unemploymentTaxRateAvailable = availableState?.unemployment_tax_rate || "N/A";
    const diNeededAvailable = availableState?.di_handling || "N/A";
    const diEmployerRateAvailable = availableState?.employer_rate || "N/A";

    // Generate forms only if any of the statuses fail
    const missingFields = [];
    if (withholdingRequired !== "N/A" && withholdingAvailable === "N/A") {
        missingFields.push("Withholding Account Number");
    }
    if (
        unemploymentRequired !== "N/A" &&
        (unemploymentAvailable === "N/A" || unemploymentTaxRateAvailable === "N/A")
    ) {
        missingFields.push("UIA Account Number or Unemployment Tax Rate");
    }
    if (diNeededRequired !== "N/A" && diNeededAvailable === "N/A" && diEmployerRateAvailable === "N/A") {
        missingFields.push("DI Handling or Employer Rate");
    }

    return missingFields.length > 0 ? (
        <div
            key={state}
            style={{
                margin: "10px 0",
                border: "1px solid #ccc",
                padding: "10px",
            }}
        >
            <h5>{state} - Fill Missing State Tax Information</h5>
            {missingFields.includes("Withholding Account Number") && (
                <div>
                    <label>Withholding Account Number:</label>
                    <input type="text" placeholder="Enter Withholding Account Number" />
                </div>
            )}
            {missingFields.includes("UIA Account Number or Unemployment Tax Rate") && (
                <div>
                    <label>UIA Account Number:</label>
                    <input type="text" placeholder="Enter UIA Account Number" />
                    <label>Unemployment Tax Rate:</label>
                    <input type="text" placeholder="Enter Unemployment Tax Rate" />
                </div>
            )}
            {missingFields.includes("DI Handling or Employer Rate") && (
                <div>
                    <label>DI Handling:</label>
                    <select>
                        <option>Select Handling</option>
                        <option>Employer Withdraws for DI Insurance</option>
                        <option>State Handles DI Insurance</option>
                    </select>
                    <label>Employer Rate (%):</label>
                    <input type="text" placeholder="Enter Employer Rate" />
                </div>
            )}
        </div>
    ) : null;
})}


{/* Missing Fields Form for States */}
{/* {Object.keys(narrowedData.withholdingStates).map((state) => {
    const withholdingRequired = narrowedData.withholdingStates[state] || "N/A";
    const unemploymentRequired = narrowedData.unemploymentStates[state] || "N/A";
    const diNeededRequired = narrowedData.diNeededStates[state] || "N/A";

    const availableState = stateTaxData.find((data) => data.state === state);
    const withholdingAvailable = availableState?.withholding_account_number || "N/A";
    const unemploymentAvailable = availableState?.uia_account_number || "N/A";
    const unemploymentTaxRateAvailable = availableState?.unemployment_tax_rate || "N/A";
    const diNeededAvailable = availableState?.di_handling || "N/A";
    const diEmployerRateAvailable = availableState?.employer_rate || "N/A";

    // Generate forms only if any of the statuses fail
    const missingFields = [];
    if (withholdingRequired !== "N/A" && withholdingAvailable === "N/A") {
        missingFields.push("Withholding Account Number");
    }
    if (
        unemploymentRequired !== "N/A" &&
        (unemploymentAvailable === "N/A" || unemploymentTaxRateAvailable === "N/A")
    ) {
        missingFields.push("UIA Account Number or Unemployment Tax Rate");
    }
    if (diNeededRequired !== "N/A" && diNeededAvailable === "N/A" && diEmployerRateAvailable === "N/A") {
        missingFields.push("DI Handling or Employer Rate");
    }

    return missingFields.length > 0 ? (
        <div
            key={state}
            style={{
                margin: "10px 0",
                border: "1px solid #ccc",
                padding: "10px",
            }}
        >
            <h5>{state} - Fill Missing State Tax Information</h5>
            {missingFields.includes("Withholding Account Number") && (
                <div>
                    <label>Withholding Account Number:</label>
                    <input type="text" placeholder="Enter Withholding Account Number" />
                </div>
            )}
            {missingFields.includes("UIA Account Number or Unemployment Tax Rate") && (
                <div>
                    <label>UIA Account Number:</label>
                    <input type="text" placeholder="Enter UIA Account Number" />
                    <label>Unemployment Tax Rate:</label>
                    <input type="text" placeholder="Enter Unemployment Tax Rate" />
                </div>
            )}
            {missingFields.includes("DI Handling or Employer Rate") && (
                <div>
                    <label>DI Handling:</label>
                    <select>
                        <option>Select Handling</option>
                        <option>Employer Withdraws for DI Insurance</option>
                        <option>State Handles DI Insurance</option>
                    </select>
                    <label>Employer Rate (%):</label>
                    <input type="text" placeholder="Enter Employer Rate" />
                </div>
            )}
        </div>
    ) : null;
})} */}






        </div>
    );
};

export default TaxComparisonPage;




